import { FieldValues, Path, useController } from "react-hook-form";
import {
  FormControl,
  FormErrorMessage,
  NumberInput,
  NumberInputField,
  NumberInputFieldProps,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import { Box } from "@familyzone/component-library";
import { ConfigFormLabel } from "./ConfigFormLabel";
import React from "react";
import { colors } from "../../../../theme";
import { FieldProps } from "./Common";

type Props<TFieldValues extends FieldValues, TFieldPath extends Path<TFieldValues> = Path<TFieldValues>> = {
  fieldProps: FieldProps<TFieldValues, TFieldPath>;
  inputProps?: NumberInputFieldProps;
  min?: number;
  max?: number;
};

export const NumberFieldControl = <TFieldValues extends FieldValues, TFieldPath extends Path<TFieldValues> = Path<TFieldValues>>({
  fieldProps,
  inputProps,
  min,
  max,
}: Props<TFieldValues, TFieldPath>): JSX.Element => {
  const { name, control, errors, rules, label, tooltip, testId, onBlur, onChange, disabled } = fieldProps;
  const { field } = useController({
    name,
    control,
    rules: rules,
  });

  return (
    <FormControl
      isInvalid={!!errors[name]}
      sx={{
        display: "flex",
        minHeight: "40px",
        fontSize: "14px",
        alignItems: "center",
        gap: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: "120px",
        }}
      >
        <ConfigFormLabel label={label} tooltip={tooltip} />
      </Box>
      <Box flex={1}>
        <NumberInput
          min={min}
          max={max}
          value={field.value}
          onChange={(_, valueNumber) => {
            field.onChange(isNaN(valueNumber) ? 0 : valueNumber);
            if (onChange !== undefined) {
              onChange(valueNumber);
            }
          }}
          sx={{
            width: "100%",
          }}
        >
          <NumberInputField
            data-testid={!!testId ? testId : `${name}-input`}
            sx={{
              height: "40px",
              width: "100%",
              border: `1px solid ${colors.neutrals["30"]}`,
              borderRadius: "6px",
              paddingLeft: "16px",
            }}
            // React hook form.
            disabled={disabled}
            value={field.value}
            onChange={(e) => {
              field.onChange(e);
              if (onChange !== undefined) {
                onChange(e);
              }
            }}
            onBlur={(e) => {
              field.onBlur();
              if (onBlur !== undefined) {
                onBlur(e);
              }
            }}
            name={field.name}
            ref={field.ref}
            // Forward the number input field props.
            {...inputProps}
          />
          <NumberInputStepper
            sx={{
              paddingRight: "4px",
            }}
          >
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <FormErrorMessage>{errors[name] && errors[name]?.message}</FormErrorMessage>
      </Box>
    </FormControl>
  );
};
